/**
 * <dual-message-scroll>
 * Sur mobile (< 750 px) : deux colonnes, un message visible à la fois,
 * qui défilent en alternance. Sur ordinateur : tous les messages sont
 * affichés sur une ligne (CSS uniquement) et le défilement est arrêté.
 * Réagit aux changements de taille d'écran et d'orientation.
 */
if (!customElements.get('dual-message-scroll')) {
  class DualMessageScroll extends HTMLElement {
    connectedCallback() {
      this.interval = parseInt(this.dataset.interval, 10) || 3000;
      this.duration = 500;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.lists = Array.from(this.querySelectorAll('.dual-message__list'));
      this.turn = 0;
      this.paused = false;

      this.onQueryChange = () => this.refresh();
      this.onPause = () => (this.paused = true);
      this.onResume = () => (this.paused = false);

      this.mobileQuery.addEventListener('change', this.onQueryChange);
      this.addEventListener('mouseenter', this.onPause);
      this.addEventListener('mouseleave', this.onResume);
      this.addEventListener('focusin', this.onPause);
      this.addEventListener('focusout', this.onResume);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this);
      }

      // Les polices et les icônes modifient la hauteur : on remesure à la fin du chargement
      window.addEventListener('load', this.onQueryChange, { once: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.stop();
      this.mobileQuery.removeEventListener('change', this.onQueryChange);
      this.removeEventListener('mouseenter', this.onPause);
      this.removeEventListener('mouseleave', this.onResume);
      this.removeEventListener('focusin', this.onPause);
      this.removeEventListener('focusout', this.onResume);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    refresh() {
      this.stop();
      this.reset();
      this.measure();
      if (this.mobileQuery.matches) this.start();
    }

    // Hauteur d'une ligne = hauteur du message le plus haut (évite les sauts de mise en page)
    measure() {
      if (!this.mobileQuery.matches) {
        this.style.removeProperty('--dual-message-row-height');
        return;
      }
      this.style.removeProperty('--dual-message-row-height');
      let max = 0;
      this.querySelectorAll('.dual-message__item').forEach(item => {
        max = Math.max(max, item.getBoundingClientRect().height);
      });
      if (max > 0) this.style.setProperty('--dual-message-row-height', `${Math.ceil(max)}px`);
    }

    reset() {
      this.lists.forEach(list => {
        list.style.transition = 'none';
        list.style.transform = 'translateY(0)';
        void list.offsetHeight;
        list.style.transition = '';
      });
    }

    start() {
      if (this.timer) return;
      this.timer = setInterval(() => this.tick(), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    tick() {
      if (this.paused || document.hidden) return;

      const list = this.lists[this.turn % this.lists.length];
      this.turn += 1;
      if (!list) return;

      const items = list.querySelectorAll('.dual-message__item');
      if (items.length <= 1) return;

      const step = items[0].getBoundingClientRect().height;
      const instant = this.reducedMotion.matches;

      list.style.transition = instant ? 'none' : '';
      list.style.transform = `translateY(-${step}px)`;

      setTimeout(
        () => {
          list.style.transition = 'none';
          list.style.transform = 'translateY(0)';
          list.appendChild(items[0]);
          void list.offsetHeight;
          list.style.transition = '';
        },
        instant ? 0 : this.duration
      );
    }
  }

  customElements.define('dual-message-scroll', DualMessageScroll);
}
